import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import React from "react";
import { BreadcrumbSeparator } from "../../atoms/BreadcrumbSeparator";

interface BreadcrumbItemProps {
  /** Folder ID */
  id: string;
  /** Folder name */
  name: string;
  /** Whether to show separator before this item */
  showSeparator: boolean;
  /** Whether drop is currently allowed */
  isDragOver?: boolean;
  /** Whether this breadcrumb is a valid destination for the active move. */
  isDropAvailable?: boolean;
  /** Called when breadcrumb is clicked */
  onNavigate: () => void;
  /** Optional glyph rendered before the name (e.g. the archive icon on a container segment) */
  icon?: React.ReactNode;
  /** Drag event handlers */
  onDragEnter?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * BreadcrumbItem - Molecule component
 *
 * Single breadcrumb with separator and drag/drop support.
 * Composed from BreadcrumbSeparator atom and Button UI component.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <BreadcrumbItem
 *   id="folder-1"
 *   name="Documents"
 *   showSeparator={true}
 *   onNavigate={handleNavigate}
 *   onDragOver={handleDragOver}
 *   onDrop={handleDrop}
 * />
 * ```
 */
export const BreadcrumbItem = React.memo(function BreadcrumbItem({
  name,
  showSeparator,
  isDragOver = false,
  isDropAvailable = false,
  onNavigate,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  className,
  icon,
}: BreadcrumbItemProps) {
  return (
    <div className={cn("flex min-w-0 items-center", className)}>
      {showSeparator && <BreadcrumbSeparator className="flex-shrink-0" />}
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "h-7 min-w-0 max-w-[12rem] gap-1.5 px-2.5 font-medium text-slate-600 transition-all duration-200",
          "hover:bg-slate-100 hover:text-slate-900",
          "active:scale-95",
          isDropAvailable && "bg-blue-50/40 ring-1 ring-inset ring-blue-200",
          isDragOver && "bg-blue-50 text-blue-700 ring-2 ring-blue-200"
        )}
        onClick={onNavigate}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        aria-label={`Navigate to ${name}`}
        title={name}
        data-drop-available={isDropAvailable || undefined}
      >
        {icon}
        {/* Long folder names ellipsise instead of pushing the trail out of the
            header row; the full name stays available in the tooltip. */}
        <span className="truncate">{name}</span>
      </Button>
    </div>
  );
});
